// Groups (circles + networks) mutations. Each hook calls the API directly via
// `getApiClient()` and patches the QueryClient cache. Server actions are not
// involved — pages prefetch via the existing `lib/views/groups.ts` fetchers and
// client interactions go through these hooks.
//
// Invalidations are scoped to the groups slice. Cross-feature effects (e.g.
// joining a circle ought to bump feed scope counts) are not handled here —
// keep the cache patches local; feed code can refresh itself on focus.

import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import {
  invalidateAndRefetchAll,
  patchAllInfiniteLists,
  patchAllLists,
  removeItemFromLists,
  restoreSnapshots,
  type SnapshotSet,
} from '../query-cache-utils';
import {
  invalidateAfterCircleNetworkLink,
  invalidateGroupAfterMutation,
} from '../cache/group-invalidation';
import {
  broadcastKeys,
  groupBroadcastScope,
  type AdminBroadcastDetail,
  type BroadcastDoc,
  type BroadcastGroupKind,
  type BroadcastPreview,
} from '../queries/broadcasts';
import {
  groupKeys,
  type CircleDetailResponse,
  type CirclePreview,
  type GroupDetail,
  type GroupImageContentType as GroupImageContentTypeFromQueries,
  type GroupImageField as GroupImageFieldFromQueries,
  type GroupImageKind,
  type GroupSummary,
  type JoinRequestView,
  type ViewerGroupRole,
} from '../queries/groups';
import { spacesKeys } from '../queries/spaces';
import { applySpaceJoinPatch } from './spaces';
import { completeOnboardingActionIfPending, onboardingKeys } from '../../components/onboarding/progress';

// Server-emitted error codes the unified members mutations surface so call
// sites can show targeted messages. The server returns HTTP 409 with one of
// these codes when the last leader would be left orphaned; everything else
// is rendered via the message from the wire envelope.
export const GROUP_MEMBER_ERROR_CODES = {
  cannotDemoteLastLeader: 'groups/cannot_demote_last_leader',
  cannotRemoveLastLeader: 'groups/cannot_remove_last_leader',
} as const;

export type JoinGroupResponse = components['schemas']['JoinGroupResponse'];

// ---------- Membership optimistic-cache helpers ----------
//
// Join / leave / withdraw / approve / decline mutations all flip the same
// derived state on a `GroupSummary`-shaped row: `viewerMembership` (member
// became/left) or `viewerJoinRequestStatus` (request opened/withdrawn).
// Both the all-circles / all-networks lists (infinite + flat shapes) and
// the per-group detail caches need to track the flip so consumer buttons
// re-derive their label from cache instantly. Helpers below own the patch
// + snapshot/rollback contract so each mutation hook stays small.

type MembershipPatch = (summary: GroupSummary) => GroupSummary;

function patchAllGroupListsInCache(
  qc: QueryClient,
  groupId: string,
  patch: MembershipPatch,
): SnapshotSet {
  const apply = (items: readonly GroupSummary[]): readonly GroupSummary[] =>
    items.map((g) => (g.id === groupId ? patch(g) : g));
  // Circles + networks share the `GroupSummary` row shape, and both register
  // under `groupKeys.<kind>.all` (covers `.lists()` infinite + `.mine()` flat
  // + locations + etc.). Pass through the `all` prefix so every cached list
  // gets patched — the predicate of `items?.[0]?.id === groupId` doesn't
  // hold because items is an unrelated shape on non-list keys; the helpers
  // filter to list-shaped values internally.
  const inf = patchAllInfiniteLists<{ items: readonly GroupSummary[] }, GroupSummary>(
    qc,
    groupKeys.all,
    apply,
  );
  const flat = patchAllLists<{ items: readonly GroupSummary[] }, GroupSummary>(
    qc,
    groupKeys.all,
    apply,
  );
  return { previousLists: [...inf.previousLists, ...flat.previousLists] };
}

// Patch the circle/network detail caches for a given id. Circles use a
// discriminated `GetCircleResponse` (visitor → `CirclePreview`, member /
// leader → `GroupDetail`); networks use a plain `GroupDetail`. Both inner
// shapes carry the same membership / join-request fields, so callers pass
// one patch function that operates on the relevant subset.
type DetailPatch = {
  membership?: 'member' | 'leader' | null;
  joinRequestStatus?: 'pending' | 'approved' | 'declined' | null;
};

function patchCircleAndNetworkDetail(
  qc: QueryClient,
  groupId: string,
  patch: DetailPatch,
): SnapshotSet {
  const previousLists: Array<readonly [readonly unknown[], unknown]> = [];

  const circleKey = groupKeys.circle.detail(groupId);
  const circle = qc.getQueryData<CircleDetailResponse>(circleKey);
  if (circle !== undefined) {
    previousLists.push([circleKey, circle]);
    if (circle.access === 'visitor') {
      const nextInner: CirclePreview = {
        ...circle.circle,
        ...(patch.joinRequestStatus !== undefined
          ? { viewerJoinRequestStatus: patch.joinRequestStatus }
          : {}),
      };
      qc.setQueryData<CircleDetailResponse>(circleKey, {
        ...circle,
        circle: nextInner,
      });
    } else {
      const nextInner: GroupDetail = {
        ...circle.circle,
        ...(patch.membership !== undefined
          ? { viewerMembership: patch.membership }
          : {}),
        ...(patch.joinRequestStatus !== undefined
          ? { viewerJoinRequestStatus: patch.joinRequestStatus }
          : {}),
      };
      qc.setQueryData<CircleDetailResponse>(circleKey, {
        ...circle,
        circle: nextInner,
      });
    }
  }

  const networkKey = groupKeys.network.detail(groupId);
  const network = qc.getQueryData<GroupDetail>(networkKey);
  if (network !== undefined) {
    previousLists.push([networkKey, network]);
    qc.setQueryData<GroupDetail>(networkKey, {
      ...network,
      ...(patch.membership !== undefined
        ? { viewerMembership: patch.membership }
        : {}),
      ...(patch.joinRequestStatus !== undefined
        ? { viewerJoinRequestStatus: patch.joinRequestStatus }
        : {}),
    });
  }

  return { previousLists };
}

// Combined: patch every list-shape + detail-shape cache for one group id.
// Returns a single rollback bag that `restoreSnapshots` reverts on error.
function applyJoinPatch(
  qc: QueryClient,
  groupId: string,
  patch: DetailPatch,
): SnapshotSet {
  const lists = patchAllGroupListsInCache(qc, groupId, (g) => ({
    ...g,
    ...(patch.membership !== undefined
      ? { viewerMembership: patch.membership }
      : {}),
    ...(patch.joinRequestStatus !== undefined
      ? { viewerJoinRequestStatus: patch.joinRequestStatus }
      : {}),
  }));
  const detail = patchCircleAndNetworkDetail(qc, groupId, patch);
  return {
    previousLists: [...lists.previousLists, ...detail.previousLists],
  };
}

// "Optimistically open a join" — at click time we don't know whether the
// server will return `joined` (open policy) or `requested` (gated). The
// summary's `joinPolicy` field tells us which to predict so the CTA flips
// to the right destination state immediately. `onSettled` invalidate +
// refetch is the final authority if the prediction was wrong.
function predictJoinPatch(
  qc: QueryClient,
  groupId: string,
): DetailPatch {
  // Find any cached summary for this group to read its joinPolicy. We
  // check lists (most common) before details because lists hold rows for
  // every group the viewer has browsed; details are 0-or-1 per id.
  type MaybeListShape = {
    readonly items?: readonly GroupSummary[];
    readonly pages?: ReadonlyArray<{ readonly items: readonly GroupSummary[] }>;
  };
  const probe = qc.getQueriesData<unknown>({ queryKey: groupKeys.all });
  for (const [, value] of probe) {
    if (value === null || typeof value !== 'object') continue;
    const v = value as MaybeListShape;
    if (v.pages !== undefined && Array.isArray(v.pages)) {
      for (const page of v.pages) {
        const hit = page.items.find((g: GroupSummary) => g.id === groupId);
        if (hit !== undefined) {
          return hit.joinPolicy === 'open'
            ? { membership: 'member' }
            : { joinRequestStatus: 'pending' };
        }
      }
    } else if (v.items !== undefined && Array.isArray(v.items)) {
      const hit = v.items.find((g: GroupSummary) => g.id === groupId);
      if (hit !== undefined) {
        return hit.joinPolicy === 'open'
          ? { membership: 'member' }
          : { joinRequestStatus: 'pending' };
      }
    }
  }
  // Fall back to detail caches.
  const network = qc.getQueryData<GroupDetail>(
    groupKeys.network.detail(groupId),
  );
  if (network !== undefined) {
    return network.joinPolicy === 'open'
      ? { membership: 'member' }
      : { joinRequestStatus: 'pending' };
  }
  const circle = qc.getQueryData<CircleDetailResponse>(
    groupKeys.circle.detail(groupId),
  );
  if (circle !== undefined) {
    return circle.circle.joinPolicy === 'open'
      ? { membership: 'member' }
      : { joinRequestStatus: 'pending' };
  }
  // Unknown policy — assume request (gated) so a wrong guess shows a pending
  // pill that the server-truth flip corrects, rather than appearing to join
  // a gated group.
  return { joinRequestStatus: 'pending' };
}

// Remove a join request row from every cached requests list for a group.
function removeJoinRequestFromCache(
  qc: QueryClient,
  groupId: string,
  kind: 'circle' | 'network',
  requestId: string,
): SnapshotSet {
  const key =
    kind === 'circle'
      ? groupKeys.circle.joinRequests(groupId)
      : groupKeys.network.joinRequests(groupId);
  return removeItemFromLists<JoinRequestView>(qc, key, requestId);
}

// ---------- Circles: create / update / lifecycle ----------

export type CreateCircleInput = {
  name: string;
  description: string;
  topicIds: readonly string[];
  format?: 'in_person' | 'virtual' | 'hybrid';
  access: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  joinPolicy?: 'open' | 'request' | 'invite_only';
  maxMembers?: number;
  language?: string;
  imageUrl?: string | null;
  /** Structured place payload from `PlacePicker`. `null` clears any prior
   *  selection. */
  place?: {
    placeId: string;
    placeText: string;
    placeLat: number;
    placeLng: number;
    placeCountryCode: string | null;
  } | null;
  affiliatedWithCompany?: boolean | null;
  affiliatedCompany?: string | null;
  // When set, the new circle is created already linked to this network.
  // The server enforces caller-is-leader of the network and that the
  // target is actually a network. Used by the network admin "Start a
  // Circle" CTA so we don't have to chain create → link from the UI.
  parentNetworkId?: string;
};

export function useCreateCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCircleInput) => {
      const client = getApiClient();
      const { data, error } = await client.POST('/api/v1/circles', {
        body: {
          name: input.name,
          description: input.description,
          topicIds: [...input.topicIds],
          format: input.format,
          access: input.access,
          minReadRole: input.minReadRole ?? 'member',
          joinPolicy: input.joinPolicy,
          maxMembers: input.maxMembers,
          language: input.language,
          imageUrl: input.imageUrl ?? undefined,
          place: input.place ?? undefined,
          affiliatedWithCompany: input.affiliatedWithCompany ?? undefined,
          affiliatedCompany: input.affiliatedCompany ?? undefined,
          parentNetworkId: input.parentNetworkId,
        },
      });
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
      // "My circles" filter view is usually inactive when a join/leave fires
      // (user is browsing all-circles). Default invalidate marks it stale but
      // doesn't refetch — the next tab switch would flash stale rows.
      invalidateAndRefetchAll(qc, { queryKey: groupKeys.circle.mine() });
      // The onboarding progress response includes `leaderCircleId` — creating a
      // circle makes the creator a leader, so the cached progress is now stale.
      // The completion screen reads `leaderCircleId` to deep-link the checklist
      // CTAs; without this invalidation it sees null and falls back to /circles.
      qc.invalidateQueries({ queryKey: onboardingKeys.progress() });
      // If the circle was created already linked to a network, refresh the
      // network's circles list so the admin surface shows the new row.
      // Prefix-match invalidates both the active and includeArchived
      // variants the key builder emits.
      if (input.parentNetworkId !== undefined) {
        qc.invalidateQueries({
          queryKey: [
            ...groupKeys.network.all,
            'circles',
            input.parentNetworkId,
          ] as const,
        });
      }
    },
  });
}

export type UpdateCircleInput = {
  circleId: string;
  name?: string;
  description?: string;
  format?: 'in_person' | 'virtual' | 'hybrid';
  access?: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  joinPolicy?: 'open' | 'request' | 'invite_only';
  maxMembers?: number | null;
  language?: string | null;
  /** Structured place payload from `PlacePicker`. `null` clears the column. */
  place?: {
    placeId: string;
    placeText: string;
    placeLat: number;
    placeLng: number;
    placeCountryCode: string | null;
  } | null;
  imageUrl?: string | null;
  logoUrl?: string | null;
  topicIds?: readonly string[];
  tags?: readonly string[];
  affiliatedWithCompany?: boolean | null;
  affiliatedCompany?: string | null;
  expectedVersion?: number;
};

export function useUpdateCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCircleInput) => {
      const client = getApiClient();
      const { data, error } = await client.PATCH('/api/v1/circles/{id}', {
        params: { path: { id: input.circleId } },
        body: {
          name: input.name,
          description: input.description,
          format: input.format,
          access: input.access,
          minReadRole: input.minReadRole ?? 'member',
          joinPolicy: input.joinPolicy,
          maxMembers: input.maxMembers,
          language: input.language,
          place: input.place,
          imageUrl: input.imageUrl,
          logoUrl: input.logoUrl,
          topicIds:
            input.topicIds !== undefined ? [...input.topicIds] : undefined,
          tags: input.tags !== undefined ? [...input.tags] : undefined,
          affiliatedWithCompany: input.affiliatedWithCompany,
          affiliatedCompany: input.affiliatedCompany,
          expectedVersion: input.expectedVersion,
        },
      });
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSuccess: (_data, vars) => {
      invalidateGroupAfterMutation(qc, 'circle', vars.circleId, 'update');
    },
  });
}

// Start the circle's group chat on demand. Idempotent server-side, so a
// double-click or a click on a circle that already has a chat just resolves
// to the existing conversation id.
export function useStartCircleGroupChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (circleId: string) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/circles/{id}/conversation',
        { params: { path: { id: circleId } } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
    onSuccess: (_data, circleId) => {
      qc.invalidateQueries({ queryKey: groupKeys.circle.conversation(circleId) });
    },
  });
}

// Group image upload — two-step flow shared between logos and covers across
// circles, networks, and spaces. After the URL collapse both routes live
// under `/groups/{id}/{cover|logo}(/commit)?` and the server resolves the
// row's kind before delegating. The `kind` arg here is still consumed for
// query-key invalidation; the URL is the same regardless. Commit returns
// the updated `GroupDetail` so consumers can show the new image immediately.
//
// `groupImageSupports(kind, field)` in `queries/groups.ts` is the canonical
// render-time gate — don't render the "Upload logo" CTA on a kind that
// doesn't support it; the server returns 422 if a client tries anyway.

/** Re-export so consumers that already import from this module don't need
 *  to chase the now-canonical query module to type a prop. */
export type GroupKind = GroupImageKind;
export type GroupImageField = GroupImageFieldFromQueries;
export type GroupImageContentType = GroupImageContentTypeFromQueries;

type UploadGroupImageResponse = components['schemas']['UploadGroupImageResponse'];
// After URL collapse, the unified `/groups/{id}/{cover|logo}/commit` route
// returns `GroupDetail` for every kind including Spaces. The server projects
// the row through `toGroupDetail`, which carries the superset of fields any
// kind reads (Spaces consumers ignore the network-only / circle-only ones).
export type CommittedGroupImage = components['schemas']['GroupDetail'];

export function useRequestGroupImageUpload(_kind: GroupKind, field: GroupImageField) {
  return useMutation({
    mutationFn: async (input: {
      identifier: string;
      contentType: GroupImageContentType;
    }): Promise<UploadGroupImageResponse> => {
      const client = getApiClient();
      const path = { id: input.identifier };
      const body = { contentType: input.contentType };
      if (field === 'logo') {
        const { data, error } = await client.POST('/api/v1/groups/{id}/logo', {
          params: { path },
          body,
        });
        if (error !== undefined || data === undefined) throwApiError(error);
        return data;
      }
      const { data, error } = await client.POST('/api/v1/groups/{id}/cover', {
        params: { path },
        body,
      });
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
  });
}

export function useCommitGroupImage(kind: GroupKind, field: GroupImageField) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      identifier: string;
      key: string;
    }): Promise<CommittedGroupImage> => {
      const client = getApiClient();
      const path = { id: input.identifier };
      const body = { key: input.key };
      if (field === 'logo') {
        const { data, error } = await client.POST(
          '/api/v1/groups/{id}/logo/commit',
          { params: { path }, body },
        );
        if (error !== undefined || data === undefined) throwApiError(error);
        return data.data;
      }
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/cover/commit',
        { params: { path }, body },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
    onSuccess: (_data, vars) => {
      if (kind === 'circle') {
        qc.invalidateQueries({ queryKey: groupKeys.circle.detail(vars.identifier) });
        qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
      } else if (kind === 'network') {
        qc.invalidateQueries({ queryKey: groupKeys.network.detail(vars.identifier) });
        qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
      } else {
        qc.invalidateQueries({ queryKey: spacesKeys.detail(vars.identifier) });
        qc.invalidateQueries({ queryKey: spacesKeys.lists() });
      }
    },
  });
}

// Kind-agnostic archive / restore. The same hooks drive circle, network,
// and space admin pages — the server resolves the group's kind from the id.
// Callers don't need to know what they're archiving.
export function useArchiveGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = getApiClient();
      const { error, response } = await client.POST(
        '/api/v1/groups/{id}/archive',
        { params: { path: { id: groupId } } },
      );
      if (error !== undefined && response.status !== 204) throwApiError(error);
    },
    onSettled: (_data, _err, groupId) => {
      // Archive endpoints are kind-agnostic — pass 'circle' as a sentinel;
      // the helper fans across all three kinds for the 'archive' cause.
      invalidateGroupAfterMutation(qc, 'circle', groupId, 'archive');
    },
  });
}

export function useRestoreGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/restore',
        { params: { path: { id: groupId } } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSettled: (_data, _err, groupId) => {
      invalidateGroupAfterMutation(qc, 'circle', groupId, 'archive');
    },
  });
}

// ---------- Group membership (request / withdraw / leave / decisions) ----------
//
// Unified hooks across all three kinds (circle / network / space). The
// underlying `POST /api/v1/groups/{id}/join` endpoint is kind-agnostic —
// the service dispatches on `groups.kind` internally for any kind-specific
// side effects. The hook still takes `kind` because the cache-patching
// logic differs: circles + networks share `applyJoinPatch` (predicts the
// outcome from cached `joinPolicy`), spaces use `applySpaceJoinPatch`
// because space summaries carry `viewerRole` instead of `viewerMembership`.

export type JoinGroupInput = {
  readonly id: string;
  readonly kind: GroupKind;
  readonly message?: string;
};

export function useJoinGroup(): UseMutationResult<
  JoinGroupResponse,
  Error,
  JoinGroupInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, message }) => {
      const client = getApiClient();
      const body =
        message !== undefined && message.length > 0 ? { message } : {};
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/join',
        { params: { path: { id } }, body },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onMutate: async ({ id, kind }) => {
      await qc.cancelQueries({
        queryKey: kind === 'space' ? spacesKeys.all : groupKeys.all,
      });
      await qc.cancelQueries({ queryKey: groupKeys.viewerRole(id) });
      const prevRole = qc.getQueryData<ViewerGroupRole>(
        groupKeys.viewerRole(id),
      );
      if (kind === 'space') {
        // Spaces don't have a request-pending state on the summary; the
        // server returns `joined` for open spaces and a 4xx for gated ones.
        // We optimistically flip to `member` and let `onSettled` correct on
        // failure.
        const snapshot = applySpaceJoinPatch(qc, id, 'member');
        qc.setQueryData<ViewerGroupRole>(groupKeys.viewerRole(id), 'member');
        return { snapshot, prevRole };
      }
      const patch = predictJoinPatch(qc, id);
      const snapshot = applyJoinPatch(qc, id, patch);
      if (patch.membership !== undefined) {
        qc.setQueryData<ViewerGroupRole>(
          groupKeys.viewerRole(id),
          patch.membership,
        );
      }
      return { snapshot, prevRole };
    },
    onError: (_err, { id }, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
      if (ctx?.prevRole !== undefined) {
        qc.setQueryData(groupKeys.viewerRole(id), ctx.prevRole);
      }
    },
    onSuccess: (data, { kind }) => {
      // Circles and networks: requesting counts — the user took the action.
      // Spaces: require actual membership before marking done.
      const groupComplete = kind !== 'space' && (data.kind === 'joined' || data.kind === 'requested');
      const spaceComplete = kind === 'space' && data.kind === 'joined';
      if (!groupComplete && !spaceComplete) return;
      const actionKey =
        kind === 'circle' ? 'joined_circle'
        : kind === 'network' ? 'joined_network'
        : 'joined_group';
      void completeOnboardingActionIfPending(qc, actionKey);
    },
    onSettled: (_data, _err, { id, kind }) => {
      invalidateGroupAfterMutation(qc, kind, id, 'join');
    },
  });
}

// Unified leave hook for all three group kinds (Circle / Network / Space).
// All three now share the `/groups/{id}/leave` endpoint; the server
// dispatches on `groups.kind` for per-kind side effects (Circle conversation
// cleanup, Space's last-leader invariant). The `kind` argument stays on the
// hook input for cache-patching — the viewer-role cache
// (`groupKeys.viewerRole(id)`) is shared, but the per-kind detail / list
// keys aren't.
export type LeaveGroupInput = {
  readonly id: string;
  readonly kind: GroupKind;
};

export function useLeaveGroup(): UseMutationResult<void, Error, LeaveGroupInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: LeaveGroupInput) => {
      const client = getApiClient();
      const { error, response } = await client.POST(
        '/api/v1/groups/{id}/leave',
        { params: { path: { id } } },
      );
      if (error !== undefined && response.status !== 204) throwApiError(error);
    },
    onMutate: async ({ id, kind }) => {
      await qc.cancelQueries({
        queryKey: kind === 'space' ? spacesKeys.all : groupKeys.all,
      });
      await qc.cancelQueries({ queryKey: groupKeys.viewerRole(id) });
      const prevRole = qc.getQueryData<ViewerGroupRole>(
        groupKeys.viewerRole(id),
      );
      qc.setQueryData<ViewerGroupRole>(groupKeys.viewerRole(id), null);
      const snapshot =
        kind === 'space'
          ? applySpaceJoinPatch(qc, id, null)
          : applyJoinPatch(qc, id, { membership: null });
      return { snapshot, prevRole };
    },
    onError: (_err, { id }, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
      if (ctx?.prevRole !== undefined) {
        qc.setQueryData(groupKeys.viewerRole(id), ctx.prevRole);
      }
    },
    onSettled: (_data, _err, { id, kind }) => {
      invalidateGroupAfterMutation(qc, kind, id, 'leave');
    },
  });
}

export type WithdrawJoinRequestInput = {
  readonly id: string;
  readonly kind: 'circle' | 'network';
};

export function useWithdrawJoinRequest(): UseMutationResult<
  void,
  Error,
  WithdrawJoinRequestInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const client = getApiClient();
      const { error, response } = await client.DELETE(
        '/api/v1/groups/{id}/join-requests/mine',
        { params: { path: { id } } },
      );
      if (error !== undefined && response.status !== 204) throwApiError(error);
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: groupKeys.all });
      const snapshot = applyJoinPatch(qc, id, { joinRequestStatus: null });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
    },
    onSettled: (_data, _err, { id, kind }) => {
      qc.invalidateQueries({ queryKey: groupKeys.myPendingJoinRequests() });
      // Detail only — withdraw doesn't change members/lists. Use the
      // kind-specific detail key.
      if (kind === 'circle') {
        qc.invalidateQueries({ queryKey: groupKeys.circle.detail(id) });
      } else {
        qc.invalidateQueries({ queryKey: groupKeys.network.detail(id) });
      }
    },
  });
}

// ---------- Leader-side: approve / decline join requests ----------

export type ApproveJoinRequestInput = {
  readonly groupId: string;
  readonly requestId: string;
  readonly kind: 'circle' | 'network';
};

export type DeclineJoinRequestInput = ApproveJoinRequestInput & {
  readonly reason?: string;
};

export function useApproveJoinRequest(): UseMutationResult<
  unknown,
  Error,
  ApproveJoinRequestInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, requestId }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/join-requests/{reqId}/approve',
        { params: { path: { id: groupId, reqId: requestId } } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onMutate: async ({ groupId, requestId, kind }) => {
      await qc.cancelQueries({
        queryKey:
          kind === 'circle'
            ? groupKeys.circle.joinRequests(groupId)
            : groupKeys.network.joinRequests(groupId),
      });
      const snapshot = removeJoinRequestFromCache(qc, groupId, kind, requestId);
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
    },
    onSettled: (_data, _err, { groupId, kind }) => {
      invalidateGroupAfterMutation(qc, kind, groupId, 'request-decision');
    },
  });
}

export function useDeclineJoinRequest(): UseMutationResult<
  unknown,
  Error,
  DeclineJoinRequestInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, requestId, reason }) => {
      const client = getApiClient();
      const body = reason !== undefined ? { reason } : {};
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/join-requests/{reqId}/decline',
        { params: { path: { id: groupId, reqId: requestId } }, body },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onMutate: async ({ groupId, requestId, kind }) => {
      await qc.cancelQueries({
        queryKey:
          kind === 'circle'
            ? groupKeys.circle.joinRequests(groupId)
            : groupKeys.network.joinRequests(groupId),
      });
      const snapshot = removeJoinRequestFromCache(qc, groupId, kind, requestId);
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
    },
    onSettled: (_data, _err, { groupId, kind }) => {
      // Decline doesn't change members or detail — just refresh the
      // requests list. Use the narrow per-kind key to avoid a wider
      // invalidation than necessary.
      qc.invalidateQueries({
        queryKey:
          kind === 'circle'
            ? groupKeys.circle.joinRequests(groupId)
            : groupKeys.network.joinRequests(groupId),
      });
    },
  });
}

// ---------- Group members management (unified across circles/networks/spaces) ----------
//
// The endpoints themselves are kind-agnostic — the server resolves kind from
// the group id. We pass 'circle' to `invalidateGroupAfterMutation` as a
// sentinel; the helper's 'members' branch fans across all three kinds because
// it doesn't know which cache holds the row.

export function useSetGroupMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      groupId: string;
      userId: string;
      role: 'member' | 'leader';
    }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/members/{userId}/role',
        {
          params: { path: { id: input.groupId, userId: input.userId } },
          body: { role: input.role },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSettled: (_data, _err, vars) =>
      invalidateGroupAfterMutation(qc, 'circle', vars.groupId, 'members'),
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; userId: string }) => {
      const client = getApiClient();
      const { error, response } = await client.DELETE(
        '/api/v1/groups/{id}/members/{userId}',
        { params: { path: { id: input.groupId, userId: input.userId } } },
      );
      if (error !== undefined && response.status !== 204) throwApiError(error);
    },
    onSettled: (_data, _err, vars) =>
      invalidateGroupAfterMutation(qc, 'circle', vars.groupId, 'members'),
  });
}

// ---------- Group invites (circle + network) ----------
//
// Both /api/v1/circles/{id}/* and /api/v1/networks/{id}/* expose the same
// per-recipient invite surfaces (lifted into shared services on the server
// in `service/group-invites.ts`). These hooks branch on `kind` so the
// `InviteMemberModal` can serve both kinds from one mount.

type GroupKindForInvites = 'circle' | 'network';

function inviteNoteKey(kind: GroupKindForInvites, groupId: string) {
  return kind === 'circle'
    ? groupKeys.circle.inviteNote(groupId)
    : groupKeys.network.inviteNote(groupId);
}

// Persist the viewer's invite-blurb note for a (kind, groupId). Empty string
// deletes the row server-side; we still cache it as '' so subsequent reads
// don't re-fetch.
export function useSetGroupInviteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: GroupKindForInvites;
      groupId: string;
      note: string;
    }): Promise<string> => {
      const client = getApiClient();
      const body = { note: input.note };
      if (input.kind === 'circle') {
        const { data, error } = await client.PUT(
          '/api/v1/circles/{id}/invite-prefs',
          { params: { path: { id: input.groupId } }, body },
        );
        if (error !== undefined || data === undefined) throwApiError(error);
        return data.note;
      }
      const { data, error } = await client.PUT(
        '/api/v1/networks/{id}/invite-prefs',
        { params: { path: { id: input.groupId } }, body },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.note;
    },
    onSuccess: (note, vars) => {
      qc.setQueryData(inviteNoteKey(vars.kind, vars.groupId), note);
    },
  });
}

export function useCreateGroupInvites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: GroupKindForInvites;
      groupId: string;
      emails: readonly string[];
      roleOnAccept?: 'member' | 'leader';
      note?: string;
    }) => {
      const client = getApiClient();
      const body = {
        emails: [...input.emails],
        roleOnAccept: input.roleOnAccept ?? 'member',
        ...(input.note !== undefined ? { note: input.note } : {}),
      };
      if (input.kind === 'circle') {
        const { data, error } = await client.POST(
          '/api/v1/circles/{id}/invites',
          { params: { path: { id: input.groupId } }, body },
        );
        if (error !== undefined || data === undefined) throwApiError(error);
        return data;
      }
      const { data, error } = await client.POST(
        '/api/v1/networks/{id}/invites',
        { params: { path: { id: input.groupId } }, body },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSettled: (data, _err, vars) => {
      // When any email matched an existing platform user, the server added
      // them straight to the group (`added`); the member list + group
      // detail / member count need to refresh so the leader sees them
      // appear without a manual reload. Off-platform-only batches skip
      // this since memberships didn't change.
      if (data !== undefined && data.added.length > 0) {
        invalidateGroupAfterMutation(qc, vars.kind, vars.groupId, 'members');
      }
    },
  });
}

// Unified invite redemption. The token is opaque — server resolves it to a
// row in `group_invites` and grants membership in the named group. Returns
// `{ membership, group, alreadyMember }`; callers use `group.kind` +
// `group.id` to navigate the new member into their group.
export type AcceptInviteResponse =
  components['schemas']['InviteAcceptResponse'];

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      token: string;
    }): Promise<AcceptInviteResponse> => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/invites/{token}/accept',
        { params: { path: { token: input.token } } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSuccess: () => {
      // The viewer's group membership list shifts after redemption — easier
      // to invalidate the whole groups slice + identity than to surgically
      // patch every list/detail entry the new membership touches.
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: ['identity'] });
    },
  });
}

// ---------- Group broadcasts (circle / network / space) ----------
//
// Group leaders create broadcasts under the per-scope `/circles|networks|
// spaces/{id}/broadcasts` URL family. Updates and previews use the canonical
// `/broadcasts/{id}` family — same URL the admin uses; Path B authz scopes
// the row by `audience_group_id`. The hooks below carry `kind`/`groupId`
// purely to derive the cache key, not to compose the URL.

type SendVia = 'email' | 'email_in_app';

export interface CreateGroupBroadcastInput {
  kind: BroadcastGroupKind;
  groupId: string;
  subject: string;
  bodyDoc: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
  mode: 'draft' | 'send' | 'schedule';
  scheduledSendAt?: string;
}

export function useCreateGroupBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupBroadcastInput) => {
      const client = getApiClient();
      // The server's group-broadcast Zod schema models bodyDoc as
      // `z.object({}).passthrough()` (to avoid a layering cycle into the
      // platform doc schema), which the OpenAPI generator narrows to
      // `Record<string, never>`. The runtime contract is the full doc, so
      // we cast at this boundary — the platform doc schema validates it
      // server-side before any DB write.
      const body = {
        subject: input.subject,
        bodyDoc: input.bodyDoc as unknown as Record<string, never>,
        sendVia: 'email_in_app' as const,
        mode: input.mode,
        ...(input.preheaderText !== undefined
          ? { preheaderText: input.preheaderText }
          : {}),
        ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
        ...(input.scheduledSendAt !== undefined
          ? { scheduledSendAt: input.scheduledSendAt }
          : {}),
      } as const;
      const { data, error } = await client.POST(
        '/api/v1/groups/{id}/broadcasts',
        { params: { path: { id: input.groupId } }, body },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSuccess: (_data, vars) => {
      invalidateGroupAfterMutation(qc, vars.kind, vars.groupId, 'broadcast');
    },
  });
}

export interface UpdateGroupBroadcastInput {
  kind: BroadcastGroupKind;
  groupId: string;
  broadcastId: string;
  version: number;
  sendVia?: SendVia;
  subject?: string;
  bodyDoc?: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
  scheduledSendAt?: string | null;
}

export function useUpdateGroupBroadcast(): UseMutationResult<
  AdminBroadcastDetail,
  Error,
  UpdateGroupBroadcastInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      const { data, error } = await client.PATCH(
        '/api/v1/broadcasts/{id}',
        {
          params: { path: { id: input.broadcastId } },
          body: {
            version: input.version,
            ...(input.sendVia !== undefined ? { sendVia: input.sendVia } : {}),
            ...(input.subject !== undefined ? { subject: input.subject } : {}),
            ...(input.bodyDoc !== undefined ? { bodyDoc: input.bodyDoc } : {}),
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
            ...(input.scheduledSendAt !== undefined
              ? { scheduledSendAt: input.scheduledSendAt }
              : {}),
          },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
    onSuccess: (data, vars) => {
      const scope = groupBroadcastScope(vars.kind, vars.groupId);
      qc.setQueryData(broadcastKeys.detail(scope, vars.broadcastId), data);
      invalidateGroupAfterMutation(qc, vars.kind, vars.groupId, 'broadcast');
    },
  });
}

export interface PreviewGroupBroadcastInput {
  kind: BroadcastGroupKind;
  groupId: string;
  broadcastId: string;
  bodyDoc?: BroadcastDoc;
  subject?: string;
  preheaderText?: string | null;
  firstName?: string;
}

export function usePreviewGroupBroadcast(): UseMutationResult<
  BroadcastPreview,
  Error,
  PreviewGroupBroadcastInput
> {
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/broadcasts/{id}/preview',
        {
          params: { path: { id: input.broadcastId } },
          body: {
            ...(input.bodyDoc !== undefined ? { bodyDoc: input.bodyDoc } : {}),
            ...(input.subject !== undefined ? { subject: input.subject } : {}),
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.firstName !== undefined
              ? { firstName: input.firstName }
              : {}),
          },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
  });
}

// ---------- Networks: create / update / posts ----------

export type CreateNetworkInput = {
  name: string;
  description: string;
  region: 'americas' | 'apac' | 'emea' | 'global_affinity';
  access?: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  tagline?: string;
  mission?: string;
  welcomeMessage?: string;
  memberRules?: string;
  joinInstructions?: string;
  tags?: readonly string[];
  // Strict object — the server's SocialLinksSchema rejects unknown keys and
  // empty strings. Omit a key to leave that link unset.
  socialLinks?: {
    website?: string;
    linkedin?: string;
    instagram?: string;
    facebook?: string;
    twitter?: string;
    email?: string;
  };
};

export function useCreateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNetworkInput) => {
      const client = getApiClient();
      const { data, error } = await client.POST('/api/v1/networks', {
        body: {
          name: input.name,
          description: input.description,
          region: input.region,
          access: input.access,
          minReadRole: input.minReadRole,
          tagline: input.tagline,
          mission: input.mission,
          welcomeMessage: input.welcomeMessage,
          memberRules: input.memberRules,
          joinInstructions: input.joinInstructions,
          tags: input.tags !== undefined ? [...input.tags] : undefined,
          socialLinks: input.socialLinks,
        },
      });
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
      // "My networks" filter view is usually inactive when a create fires
      // (admin is browsing /networks). Default invalidate marks it stale but
      // doesn't refetch — the next tab switch would flash stale rows.
      invalidateAndRefetchAll(qc, { queryKey: groupKeys.network.mine() });
    },
  });
}

export type NetworkLeaderApplicationInput = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  countryCode?: string;
  region: 'EMEA' | 'Americas' | 'APAC' | 'Global Affinity';
  phone?: string;
  circles?: string;
  otherCommunity?: string;
  communitySize?: string;
  proposedName: string;
  city: string;
  whyCreate: string;
  eventExperience: string;
  leadershipPlan: string;
};

export function useSubmitNetworkLeaderApplication() {
  return useMutation({
    mutationFn: async (input: NetworkLeaderApplicationInput) => {
      const client = getApiClient();
      const { error } = await client.POST('/api/v1/networks/apply', { body: input });
      if (error !== undefined) throwApiError(error);
    },
  });
}

export type UpdateNetworkInput = {
  networkId: string;
  name?: string;
  description?: string;
  access?: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  imageUrl?: string | null;
  topicIds?: readonly string[];
  region?: 'americas' | 'apac' | 'emea' | 'global_affinity';
  // The admin surface supports additional fields the schema accepts.
  tagline?: string | null;
  mission?: string | null;
  welcomeMessage?: string | null;
  memberRules?: string | null;
  joinInstructions?: string | null;
  logoUrl?: string | null;
  tags?: readonly string[];
  // Strict object — the server's SocialLinksSchema rejects unknown keys. Use
  // an empty object (not undefined) to wipe all social links in one go.
  socialLinks?: {
    website?: string;
    linkedin?: string;
    instagram?: string;
    facebook?: string;
    twitter?: string;
    email?: string;
  };
};

export function useUpdateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateNetworkInput) => {
      const client = getApiClient();
      const { data, error } = await client.PATCH('/api/v1/networks/{id}', {
        params: { path: { id: input.networkId } },
        body: {
          name: input.name,
          description: input.description,
          access: input.access,
          minReadRole: input.minReadRole,
          imageUrl: input.imageUrl,
          topicIds:
            input.topicIds !== undefined ? [...input.topicIds] : undefined,
          region: input.region,
          tagline: input.tagline,
          mission: input.mission,
          welcomeMessage: input.welcomeMessage,
          memberRules: input.memberRules,
          joinInstructions: input.joinInstructions,
          logoUrl: input.logoUrl,
          tags: input.tags !== undefined ? [...input.tags] : undefined,
          socialLinks: input.socialLinks,
        },
      });
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSuccess: (_data, vars) => {
      invalidateGroupAfterMutation(qc, 'network', vars.networkId, 'update');
    },
  });
}

// ---------- Networks: link / unlink circles ----------

export function useLinkCircleToNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { networkId: string; circleId: string }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/networks/{id}/circles',
        {
          params: { path: { id: input.networkId } },
          body: { circleId: input.circleId },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data;
    },
    onSettled: (_data, _err, vars) => {
      invalidateAfterCircleNetworkLink(qc, vars.networkId, vars.circleId);
    },
  });
}

export function useUnlinkCircleFromNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { networkId: string; circleId: string }) => {
      const client = getApiClient();
      const { error, response } = await client.DELETE(
        '/api/v1/networks/{id}/circles/{circleId}',
        {
          params: {
            path: { id: input.networkId, circleId: input.circleId },
          },
        },
      );
      if (error !== undefined && response.status !== 204) throwApiError(error);
    },
    onSettled: (_data, _err, vars) => {
      invalidateAfterCircleNetworkLink(qc, vars.networkId, vars.circleId);
    },
  });
}

// ---------- Network resources (featured / delete) ----------

export function useFeatureNetworkResource() {
  return useMutation({
    mutationFn: async (input: {
      networkId: string;
      resourceId: string;
      featured: boolean;
    }) => {
      const client = getApiClient();
      if (input.featured) {
        const { error, response } = await client.POST(
          '/api/v1/networks/{id}/resources/{resourceId}/feature',
          {
            params: {
              path: { id: input.networkId, resourceId: input.resourceId },
            },
          },
        );
        if (error !== undefined || !response.ok) throwApiError(error);
      } else {
        const { error, response } = await client.DELETE(
          '/api/v1/networks/{id}/resources/{resourceId}/feature',
          {
            params: {
              path: { id: input.networkId, resourceId: input.resourceId },
            },
          },
        );
        if (!response.ok) throwApiError(error);
      }
    },
  });
}

export function useDeleteNetworkResource() {
  return useMutation({
    mutationFn: async (input: {
      networkId: string;
      resourceId: string;
    }) => {
      const client = getApiClient();
      const { error, response } = await client.DELETE(
        '/api/v1/networks/{id}/resources/{resourceId}',
        {
          params: {
            path: { id: input.networkId, resourceId: input.resourceId },
          },
        },
      );
      if (!response.ok) throwApiError(error);
    },
  });
}

// Re-export the JoinRequestView so consumers don't need to chase down the
// schema namespace just to type a row.
export type { JoinRequestView };
